/**
 * Role definitions and assignments — sv_permissions' catalogue, as the gateway
 * serializes it.
 *
 * Two shapes here regularly surprise a first reader.
 *
 * **`name` and `description` are locale MAPS, not strings.** `RoleDefinitionInfo`
 * carries them as `map<string, string>` end to end, so the wire is
 * `{"en": "Owner", "ru": "Владелец"}`. Rendering one straight into a label prints
 * `[object Object]`; {@link roleLabel} is the reader.
 *
 * **`domain_type` arrives as either an integer or a name.** The catalogue types it as
 * the numeric enum (0/1/2) and an assignment row has been seen carrying the string
 * ("company"). Both are accepted here and normalized by {@link domainTypeOf}, because
 * the alternative — picking one and being wrong — silently empties every filter built
 * on it.
 */

/** 0 = system, 1 = company, 2 = department. */
export const DOMAIN_TYPE_SYSTEM = 0;
export const DOMAIN_TYPE_COMPANY = 1;
export const DOMAIN_TYPE_DEPARTMENT = 2;

export type DomainType =
  | typeof DOMAIN_TYPE_SYSTEM
  | typeof DOMAIN_TYPE_COMPANY
  | typeof DOMAIN_TYPE_DEPARTMENT;

/** BCP-47 short tag → the role's name in that language. Keys are not guaranteed. */
export type LocaleMap = Record<string, string>;

/** GET /roles/definitions — one entry of the catalogue. */
export type RoleDefinition = {
  code: string;
  name: LocaleMap;
  description: LocaleMap;
  domain_type: number | string;
  priority: number;
  is_system: boolean;
  /**
   * `false` means `POST /roles/assign` refuses this code outright. `owner` is the one
   * that matters here: it is seeded but not assignable, so a role picker that offers
   * it hands the user a button whose only outcome is an error.
   */
  is_assignable: boolean;
};

export type RoleDefinitions = {roles?: RoleDefinition[]};

/** GET /roles/users/{user_id} — one row of `role_assignments`. */
export type RoleAssignment = {
  id: string;
  user_id: string;
  role_code: string;
  domain_type: number | string;
  company_id: string;
  department_id: string;
  assigned_by: string;
  assigned_at: string;
  is_active: boolean;
};

export type RoleAssignments = {roles?: RoleAssignment[]};

/**
 * GET /roles/{role_code}/users — one holder.
 *
 * The account fields the gateway joins in are optional on the wire: a row whose
 * account sv_auth cannot resolve still comes back, identified by id alone.
 */
/**
 * One row of `GET /roles/{role_code}/users`.
 *
 * ⚠️ A row is an ASSIGNMENT, not a person, and the list is platform-wide: `x-company-id`
 * picks the Casbin domain the caller is checked in, it does not filter the answer. A
 * company owner asking for `owner` gets every owner of every company, each row saying
 * in `company_id` whose it is. Read that field before believing a row belongs here.
 */
export type RoleHolder = {
  id?: string;
  user_id?: string;
  username?: string;
  email?: string;
  phone?: string;
  full_name?: string;
  role_code?: string;
  domain_type?: number | string;
  company_id?: string;
  department_id?: string;
  assigned_at?: string;
  is_active?: boolean;
};

/**
 * The holders envelope.
 *
 * ⚠️ `page_size`, not `per_page` — this route is the one place in the gateway that
 * spells the page length the way sv_permissions does. A paginator written against the
 * house convention reads `undefined` here and concludes there is one page.
 */
export type RoleHolders = {
  users?: RoleHolder[];
  total?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
};

/** Normalize either spelling of `domain_type` to the numeric enum. */
export function domainTypeOf(value: number | string | undefined): number {
  if (typeof value === 'number') {
    return value;
  }
  switch (String(value ?? '').toLowerCase()) {
    case 'system':
    case '0':
      return DOMAIN_TYPE_SYSTEM;
    case 'company':
    case '1':
      return DOMAIN_TYPE_COMPANY;
    case 'department':
    case '2':
      return DOMAIN_TYPE_DEPARTMENT;
    default:
      return DOMAIN_TYPE_COMPANY;
  }
}

/**
 * The role's name in the user's language.
 *
 * Falls through language → Russian → English → the code itself, and the last step is
 * the one that matters: a role the catalogue grew after this build shipped still
 * renders as something a human can read back to support, rather than as a blank chip.
 */
export function roleLabel(
  def: Pick<RoleDefinition, 'code' | 'name'>,
  language: string,
): string {
  const map = def.name ?? {};
  const short = language.split('-')[0];
  return map[language] ?? map[short] ?? map.ru ?? map.en ?? def.code;
}

/** The same fall-through for the longer text under the name. */
export function roleDescription(
  def: Pick<RoleDefinition, 'description'>,
  language: string,
): string {
  const map = def.description ?? {};
  const short = language.split('-')[0];
  return map[language] ?? map[short] ?? map.ru ?? map.en ?? '';
}
